import React, { useState } from 'react';
import { Plus, X, MapPin, Calendar, Home, Activity, ArrowRight, Check, Globe, Mail, Phone, MessageCircle } from 'lucide-react';
import { bestemmingen, accommodatieTypes, activiteiten } from '../mockData';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = '+31624359764';

const RondreizenTool = () => {
  const [reisplan, setReisplan] = useState({
    naam: '',
    bestemmingen: [],
    duur: '',
    duurAnders: '',
    reisType: '', // 'rondreizen', 'op1plek', 'combinatie'
    budget: '',
    accommodatie: '',
    activiteiten: [],
    andereActiviteit: '',
    email: '',
    telefoon: ''
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeContinent, setActiveContinent] = useState('europa');

  const continenten = [
    { id: 'europa', naam: '🇪🇺 Europa', count: bestemmingen.europa.length },
    { id: 'azie', naam: '🌏 Azië', count: bestemmingen.azie.length },
    { id: 'afrika', naam: '🌍 Afrika', count: bestemmingen.afrika.length },
    { id: 'amerika', naam: '🌎 Amerika', count: bestemmingen.amerika.length },
    { id: 'oceanie', naam: '🌏 Oceanië', count: bestemmingen.oceanie.length }
  ];

  const handleAddBestemming = (bestemming) => {
    if (!reisplan.bestemmingen.includes(bestemming)) {
      setReisplan({
        ...reisplan,
        bestemmingen: [...reisplan.bestemmingen, bestemming]
      });
    }
  };

  const handleRemoveBestemming = (bestemming) => {
    setReisplan({
      ...reisplan,
      bestemmingen: reisplan.bestemmingen.filter(b => b !== bestemming)
    });
  };

  const handleToggleActiviteit = (activiteit) => {
    if (reisplan.activiteiten.includes(activiteit)) {
      setReisplan({
        ...reisplan,
        activiteiten: reisplan.activiteiten.filter(a => a !== activiteit)
      });
    } else {
      setReisplan({
        ...reisplan,
        activiteiten: [...reisplan.activiteiten, activiteit]
      });
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/rondreizen-aanvraag`, reisplan);
      
      if (response.status === 200) {
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          // Reset form
          setReisplan({
            naam: '',
            bestemmingen: [],
            duur: '',
            duurAnders: '',
            reisType: '',
            budget: '',
            accommodatie: '',
            activiteiten: [],
            andereActiviteit: '',
            email: '',
            telefoon: ''
          });
          setCurrentStep(1);
        }, 5000);
      }
    } catch (err) {
      setError('Er ging iets mis bij het versturen. Probeer het opnieuw of neem direct contact op.');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppSubmit = async () => {
    setLoading(true);
    setError('');
    
    // Format the trip details for WhatsApp
    const reisTypeMap = {
      rondreizen: 'Rondreizen (meerdere plekken)',
      op1plek: 'Op 1 plek blijven'
    };
    const reisTypeText = reisTypeMap[reisplan.reisType] || 'Combinatie (rondreizen + relaxen)';
    
    const duurText = reisplan.duur === 'Anders' ? `${reisplan.duurAnders}` : reisplan.duur;
    
    const activiteitenText = [...reisplan.activiteiten];
    if (reisplan.andereActiviteit) {
      activiteitenText.push(`Anders: ${reisplan.andereActiviteit}`);
    }
    
    const message = `🗺️ *Nieuwe Reis op Maat Aanvraag*

📝 *Reisnaam:* ${reisplan.naam}

📍 *Bestemmingen (${reisplan.bestemmingen.length}):*
${reisplan.bestemmingen.map(b => `• ${b}`).join('\n')}

⏱️ *Reisduur:* ${duurText}
🚗 *Type reis:* ${reisTypeText}
💰 *Budget:* ${reisplan.budget}
🏨 *Accommodatie:* ${reisplan.accommodatie}

🎯 *Activiteiten (${activiteitenText.length}):*
${activiteitenText.map(a => `• ${a}`).join('\n')}

👤 *Contactgegevens:*
📧 Email: ${reisplan.email}
📱 Telefoon: ${reisplan.telefoon}

---
Verzonden via Nikita's Reizen op Maat`;

    try {
      // Save to database first
      await axios.post(`${BACKEND_URL}/api/rondreizen-aanvraag`, reisplan);
      
      // Encode the message for URL
      const encodedMessage = encodeURIComponent(message);
      
      // Create WhatsApp link
      const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}?text=${encodedMessage}`;
      
      // Direct redirect - GEEN popup, werkt altijd
      window.location.href = whatsappUrl;
      
    } catch (err) {
      setError('Er ging iets mis. Probeer het opnieuw.');
      setLoading(false);
    }
  };

  const isStepComplete = (step) => {
    switch(step) {
      case 1:
        return reisplan.naam && reisplan.bestemmingen.length > 0;
      case 2:
        const duurOK = reisplan.duur && (reisplan.duur !== 'Anders' || reisplan.duurAnders);
        return duurOK && reisplan.reisType;
      case 3:
        return reisplan.budget && reisplan.accommodatie;
      case 4:
        return reisplan.activiteiten.length > 0 || reisplan.andereActiviteit;
      case 5:
        return reisplan.email && reisplan.telefoon;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-blue-950 via-blue-900 to-blue-800 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Plan Je Reis op Maat
            </h1>
            <p className="text-xl text-blue-100">
              Stel stap voor stap je droomreis samen - rondreizen of relaxen op één plek
            </p>
          </div>
        </div>
      </section>

      {/* Progress Steps */}
      <section className="py-8 bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-2 md:gap-4 max-w-4xl mx-auto">
            {[
              { num: 1, label: 'Bestemming' },
              { num: 2, label: 'Duur & Type' },
              { num: 3, label: 'Budget & Hotel' },
              { num: 4, label: 'Activiteiten' },
              { num: 5, label: 'Contact' }
            ].map((step, index) => (
              <React.Fragment key={step.num}>
                <div
                  className={`flex items-center gap-2 cursor-pointer ${
                    currentStep === step.num ? 'opacity-100' : 'opacity-60'
                  }`}
                  onClick={() => setCurrentStep(step.num)}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all duration-300 ${
                      isStepComplete(step.num)
                        ? 'bg-green-500 text-white'
                        : currentStep === step.num
                        ? 'bg-blue-900 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {isStepComplete(step.num) ? <Check className="w-5 h-5" /> : step.num}
                  </div>
                  <span className="hidden md:block text-sm font-medium text-gray-700">
                    {step.label}
                  </span>
                </div>
                {index < 4 && (
                  <div className="w-8 md:w-16 h-1 bg-gray-200">
                    <div
                      className={`h-full transition-all duration-300 ${
                        isStepComplete(step.num) ? 'bg-green-500' : 'bg-gray-200'
                      }`}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Step 1: Bestemmingen */}
            {currentStep === 1 && (
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center gap-3 mb-6">
                  <MapPin className="w-8 h-8 text-blue-900" />
                  <h2 className="text-3xl font-bold text-blue-900">Kies Je Bestemmingen</h2>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Geef je reis een naam
                  </label>
                  <input
                    type="text"
                    value={reisplan.naam}
                    onChange={(e) => setReisplan({ ...reisplan, naam: e.target.value })}
                    placeholder="Bijv: Mijn Azië Avontuur"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>

                <p className="text-gray-600 mb-6">
                  Selecteer één of meerdere bestemmingen voor je rondreis
                </p>

                {/* Selected Bestemmingen */}
                {reisplan.bestemmingen.length > 0 && (
                  <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                    <h3 className="text-sm font-semibold text-blue-900 mb-3">
                      Geselecteerde Bestemmingen ({reisplan.bestemmingen.length})
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {reisplan.bestemmingen.map((bestemming) => (
                        <div
                          key={bestemming}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-900 text-white rounded-full"
                        >
                          <span>{bestemming}</span>
                          <button
                            onClick={() => handleRemoveBestemming(bestemming)}
                            className="hover:bg-blue-800 rounded-full p-1 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Continent Tabs */}
                <div className="mb-6">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {continenten.map((continent) => (
                      <button
                        key={continent.id}
                        onClick={() => setActiveContinent(continent.id)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                          activeContinent === continent.id
                            ? 'bg-blue-900 text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {continent.naam} ({continent.count})
                      </button>
                    ))}
                  </div>
                </div>

                {/* Available Bestemmingen per Continent */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {bestemmingen[activeContinent].map((bestemming) => (
                    <button
                      key={bestemming}
                      onClick={() => handleAddBestemming(bestemming)}
                      disabled={reisplan.bestemmingen.includes(bestemming)}
                      className={`p-4 rounded-lg border-2 font-medium transition-all duration-300 ${
                        reisplan.bestemmingen.includes(bestemming)
                          ? 'border-blue-900 bg-blue-50 text-blue-900 opacity-50 cursor-not-allowed'
                          : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50 text-gray-700'
                      }`}
                    >
                      {bestemming}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => setCurrentStep(2)}
                  disabled={!isStepComplete(1)}
                  className={`mt-8 w-full md:w-auto px-8 py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all duration-300 ${
                    isStepComplete(1)
                      ? 'bg-gradient-to-r from-blue-900 to-blue-700 text-white hover:from-blue-800 hover:to-blue-600'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Volgende Stap
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            )}

            {/* Step 2: Duur & Type Reis */}
            {currentStep === 2 && (
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center gap-3 mb-6">
                  <Calendar className="w-8 h-8 text-blue-900" />
                  <h2 className="text-3xl font-bold text-blue-900">Duur & Type Reis</h2>
                </div>

                <p className="text-gray-600 mb-8">
                  Hoe lang wil je weg en wat voor reis wil je maken?
                </p>

                <div className="space-y-8">
                  {/* Reisduur */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Hoelang wil je op reis?
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {['1 week', '2 weken', '3 weken', '1 maand', 'Anders'].map((duur) => (
                        <button
                          key={duur}
                          onClick={() => setReisplan({ ...reisplan, duur })}
                          className={`p-4 rounded-lg border-2 font-medium transition-all duration-300 ${
                            reisplan.duur === duur
                              ? 'border-blue-900 bg-blue-900 text-white'
                              : 'border-gray-300 hover:border-blue-500 text-gray-700'
                          }`}
                        >
                          {duur}
                        </button>
                      ))}
                    </div>
                    
                    {/* Anders invulveld */}
                    {reisplan.duur === 'Anders' && (
                      <div className="mt-4">
                        <input
                          type="text"
                          value={reisplan.duurAnders}
                          onChange={(e) => setReisplan({ ...reisplan, duurAnders: e.target.value })}
                          placeholder="Bijv: 10 dagen, 5 weken..."
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        />
                      </div>
                    )}
                  </div>

                  {/* Type Reis */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Wat voor type reis wil je maken?
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <button
                        onClick={() => setReisplan({ ...reisplan, reisType: 'rondreizen' })}
                        className={`p-6 rounded-lg border-2 font-medium text-left transition-all duration-300 ${
                          reisplan.reisType === 'rondreizen'
                            ? 'border-blue-900 bg-blue-900 text-white'
                            : 'border-gray-300 hover:border-blue-500 text-gray-700 hover:bg-blue-50'
                        }`}
                      >
                        <div className="flex flex-col gap-2">
                          <span className="text-2xl">🚗</span>
                          <span className="text-lg font-bold">Rondreizen</span>
                          <span className={`text-sm ${reisplan.reisType === 'rondreizen' ? 'text-blue-100' : 'text-gray-500'}`}>
                            Meerdere plekken bezoeken
                          </span>
                        </div>
                      </button>
                      
                      <button
                        onClick={() => setReisplan({ ...reisplan, reisType: 'op1plek' })}
                        className={`p-6 rounded-lg border-2 font-medium text-left transition-all duration-300 ${
                          reisplan.reisType === 'op1plek'
                            ? 'border-blue-900 bg-blue-900 text-white'
                            : 'border-gray-300 hover:border-blue-500 text-gray-700 hover:bg-blue-50'
                        }`}
                      >
                        <div className="flex flex-col gap-2">
                          <span className="text-2xl">🏖️</span>
                          <span className="text-lg font-bold">Op 1 plek</span>
                          <span className={`text-sm ${reisplan.reisType === 'op1plek' ? 'text-blue-100' : 'text-gray-500'}`}>
                            Resort of city stay
                          </span>
                        </div>
                      </button>
                      
                      <button
                        onClick={() => setReisplan({ ...reisplan, reisType: 'combinatie' })}
                        className={`p-6 rounded-lg border-2 font-medium text-left transition-all duration-300 ${
                          reisplan.reisType === 'combinatie'
                            ? 'border-blue-900 bg-blue-900 text-white'
                            : 'border-gray-300 hover:border-blue-500 text-gray-700 hover:bg-blue-50'
                        }`}
                      >
                        <div className="flex flex-col gap-2">
                          <span className="text-2xl">🌴</span>
                          <span className="text-lg font-bold">Combinatie</span>
                          <span className={`text-sm ${reisplan.reisType === 'combinatie' ? 'text-blue-100' : 'text-gray-500'}`}>
                            Rondreizen + relaxen
                          </span>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 mt-8">
                  <button
                    onClick={() => setCurrentStep(1)}
                    className="px-8 py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:border-gray-400 transition-all"
                  >
                    Vorige
                  </button>
                  <button
                    onClick={() => setCurrentStep(3)}
                    disabled={!isStepComplete(2)}
                    className={`flex-1 px-8 py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all duration-300 ${
                      isStepComplete(2)
                        ? 'bg-gradient-to-r from-blue-900 to-blue-700 text-white hover:from-blue-800 hover:to-blue-600'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Volgende Stap
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Budget & Accommodatie */}
            {currentStep === 3 && (
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center gap-3 mb-6">
                  <Home className="w-8 h-8 text-blue-900" />
                  <h2 className="text-3xl font-bold text-blue-900">Budget & Accommodatie</h2>
                </div>

                <p className="text-gray-600 mb-8">
                  Wat is je budget en welk type accommodatie geef je de voorkeur?
                </p>

                <div className="space-y-8">
                  {/* Budget */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Budget per persoon
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {[
                        '€1000 - €2000',
                        '€2000 - €4000',
                        '€4000+'
                      ].map((budget) => (
                        <button
                          key={budget}
                          onClick={() => setReisplan({ ...reisplan, budget: budget })}
                          className={`p-4 rounded-lg border-2 font-medium transition-all duration-300 ${
                            reisplan.budget === budget
                              ? 'border-blue-900 bg-blue-900 text-white'
                              : 'border-gray-300 hover:border-blue-500 text-gray-700'
                          }`}
                        >
                          {budget}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Accommodatie Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Type accommodatie
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {accommodatieTypes.map((type) => (
                        <button
                          key={type}
                          onClick={() => setReisplan({ ...reisplan, accommodatie: type })}
                          className={`p-4 rounded-lg border-2 font-medium transition-all duration-300 ${
                            reisplan.accommodatie === type
                              ? 'border-blue-900 bg-blue-900 text-white'
                              : 'border-gray-300 hover:border-blue-500 text-gray-700 hover:bg-blue-50'
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 mt-8">
                  <button
                    onClick={() => setCurrentStep(2)}
                    className="px-8 py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:border-gray-400 transition-all"
                  >
                    Vorige
                  </button>
                  <button
                    onClick={() => setCurrentStep(4)}
                    disabled={!isStepComplete(3)}
                    className={`flex-1 px-8 py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all duration-300 ${
                      isStepComplete(3)
                        ? 'bg-gradient-to-r from-blue-900 to-blue-700 text-white hover:from-blue-800 hover:to-blue-600'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Volgende Stap
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Activiteiten */}
            {currentStep === 4 && (
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center gap-3 mb-6">
                  <Activity className="w-8 h-8 text-blue-900" />
                  <h2 className="text-3xl font-bold text-blue-900">Activiteiten</h2>
                </div>

                <p className="text-gray-600 mb-6">
                  Selecteer de activiteiten die je wilt doen (meerdere mogelijk)
                </p>

                {(reisplan.activiteiten.length > 0 || reisplan.andereActiviteit) && (
                  <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                    <h3 className="text-sm font-semibold text-blue-900 mb-2">
                      {reisplan.activiteiten.length + (reisplan.andereActiviteit ? 1 : 0)} activiteit(en) geselecteerd
                    </h3>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                  {activiteiten.map((activiteit) => (
                    <button
                      key={activiteit}
                      onClick={() => handleToggleActiviteit(activiteit)}
                      className={`p-4 rounded-lg border-2 font-medium text-left transition-all duration-300 ${
                        reisplan.activiteiten.includes(activiteit)
                          ? 'border-blue-900 bg-blue-900 text-white'
                          : 'border-gray-300 hover:border-blue-500 text-gray-700 hover:bg-blue-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{activiteit}</span>
                        {reisplan.activiteiten.includes(activiteit) && (
                          <Check className="w-5 h-5" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Anders - Eigen invulveld */}
                <div className="p-6 bg-gradient-to-br from-blue-50 to-green-50 rounded-lg border-2 border-blue-200">
                  <label className="block text-sm font-semibold text-blue-900 mb-3">
                    ✏️ Andere activiteit? Vul hier in:
                  </label>
                  <input
                    type="text"
                    value={reisplan.andereActiviteit}
                    onChange={(e) => setReisplan({ ...reisplan, andereActiviteit: e.target.value })}
                    placeholder="Bijv: Kookworkshop, Helicopter tour, Paardrijden..."
                    className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>

                <div className="flex gap-4 mt-8">
                  {error && (
                    <div className="w-full p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mb-4">
                      {error}
                    </div>
                  )}
                  <button
                    onClick={() => setCurrentStep(3)}
                    className="px-8 py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:border-gray-400 transition-all"
                  >
                    Vorige
                  </button>
                  <button
                    onClick={() => setCurrentStep(5)}
                    disabled={!isStepComplete(4)}
                    className={`flex-1 px-8 py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all duration-300 ${
                      isStepComplete(4)
                        ? 'bg-gradient-to-r from-blue-900 to-blue-700 text-white hover:from-blue-800 hover:to-blue-600'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Volgende Stap
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 5: Contactgegevens */}
            {currentStep === 5 && (
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center gap-3 mb-6">
                  <Mail className="w-8 h-8 text-blue-900" />
                  <h2 className="text-3xl font-bold text-blue-900">Jouw Contactgegevens</h2>
                </div>

                <p className="text-gray-600 mb-6">
                  We nemen contact met je op om je droomreis verder uit te werken
                </p>

                <div className="space-y-6">
                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email adres <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        value={reisplan.email}
                        onChange={(e) => setReisplan({ ...reisplan, email: e.target.value })}
                        placeholder="jouw@email.nl"
                        className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        required
                      />
                    </div>
                  </div>

                  {/* Telefoon */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefoonnummer <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="tel"
                        value={reisplan.telefoon}
                        onChange={(e) => setReisplan({ ...reisplan, telefoon: e.target.value })}
                        placeholder="+31 6 12345678"
                        className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        required
                      />
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="mt-8 p-6 bg-gradient-to-br from-blue-50 to-green-50 rounded-xl border border-blue-200">
                    <h3 className="text-lg font-bold text-blue-900 mb-4">📋 Samenvatting van je reisplan</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Reisnaam:</span>
                        <span className="font-semibold text-gray-900">{reisplan.naam}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Bestemmingen:</span>
                        <span className="font-semibold text-gray-900">{reisplan.bestemmingen.length} landen</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Duur:</span>
                        <span className="font-semibold text-gray-900">
                          {reisplan.duur === 'Anders' ? reisplan.duurAnders : reisplan.duur}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Type reis:</span>
                        <span className="font-semibold text-gray-900">
                          {reisplan.reisType === 'rondreizen' ? 'Rondreizen' : 
                           reisplan.reisType === 'op1plek' ? 'Op 1 plek' : 'Combinatie'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Budget:</span>
                        <span className="font-semibold text-gray-900">{reisplan.budget}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Accommodatie:</span>
                        <span className="font-semibold text-gray-900">{reisplan.accommodatie}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Activiteiten:</span>
                        <span className="font-semibold text-gray-900">
                          {reisplan.activiteiten.length + (reisplan.andereActiviteit ? 1 : 0)} geselecteerd
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="w-full p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mt-6">
                    {error}
                  </div>
                )}

                <div className="flex gap-4 mt-8">
                  <button
                    onClick={() => setCurrentStep(4)}
                    className="px-8 py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:border-gray-400 transition-all"
                  >
                    Vorige
                  </button>
                  <button
                    onClick={handleWhatsAppSubmit}
                    disabled={!isStepComplete(5) || loading}
                    className={`flex-1 px-8 py-4 rounded-lg font-semibold flex items-center justify-center gap-3 transition-all duration-300 ${
                      isStepComplete(5) && !loading
                        ? 'bg-gradient-to-r from-green-600 to-green-500 text-white hover:from-green-500 hover:to-green-400 shadow-lg hover:shadow-xl transform hover:scale-105'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {loading ? (
                      <>
                        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Bezig met versturen...
                      </>
                    ) : (
                      <>
                        <MessageCircle className="w-6 h-6" />
                        Verstuur via WhatsApp
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Success Modal */}
      {showSuccess && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Reisplan Ontvangen!
            </h3>
            <p className="text-gray-600 mb-6">
              Bedankt voor je aanvraag! We nemen zo snel mogelijk contact met je op om je droomreis verder uit te werken.
            </p>
            <Link
              to="/contact"
              className="inline-block px-6 py-3 bg-blue-900 text-white font-semibold rounded-lg hover:bg-blue-800 transition-all"
            >
              Neem Direct Contact Op
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default RondreizenTool;
